import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Calendar, TrendingUp, Package, Target, ChevronRight, ShoppingCart, FileSpreadsheet, Zap, Info, Activity } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const V5_PROBABILISTIC_MODEL = 'v5.0.0';
const V5_FIX_I_DEFAULTS = {
  trendFilterMargin: 1000,
};

// Fix I inputs a fresh builder starts with. Live Preds prices its DEPOSIT from
// the same orders, with its own models and shifts.
export const GOOSE_FIX_I_DEFAULTS = {
  trendFilterMargin: 10,
  bucket1Volume: 40,
  bucket2Volume: 40,
  bucket3Volume: 40,
};

// Margin tiers a fresh builder starts with (IDA1 closing and Fix II Soft Kill).
// Bison Schedule prices its Soft Kill Deposit with them.
export const GOOSE_MARGIN_TIER_DEFAULTS = {
  marginTier1: 200,
  marginTier2: 300,
  marginTier3: 400,
  marginTier4: 500,
};

// v5.0.0 runs with its own trend filter margin (the effect below switches to it).
export const defaultTrendFilterMargin = (probModel) => (
  probModel === V5_PROBABILISTIC_MODEL ? V5_FIX_I_DEFAULTS.trendFilterMargin : GOOSE_FIX_I_DEFAULTS.trendFilterMargin
);

// ISO timestamp -> Warsaw wall-clock "HH:MM:SS".
export const warsawTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Europe/Warsaw' });

function GooseOrderBuilder() {
  const { axiosPost, fetchGet } = useApi();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  // Active auction tab
  const [activeTab, setActiveTab] = useState('fix_i');

  // Common fields
  const [date, setDate] = useState(tomorrowStr);
  const [baseAction, setBaseAction] = useState('');
  const [baseVolume, setBaseVolume] = useState(0);

  // Fix I & Fix II shared
  const [fixIModel, setFixIModel] = useState('v2.0.0');
  const [fixIIModel, setFixIIModel] = useState('v1.0.0-fix2');

  // Fix I specific
  const [probModelVersion, setProbModelVersion] = useState('v30-k');
  const [trendFilterMargin, setTrendFilterMargin] = useState(GOOSE_FIX_I_DEFAULTS.trendFilterMargin);
  const [bucket1Volume, setBucket1Volume] = useState(GOOSE_FIX_I_DEFAULTS.bucket1Volume);
  const [bucket2Volume, setBucket2Volume] = useState(GOOSE_FIX_I_DEFAULTS.bucket2Volume);
  const [bucket3Volume, setBucket3Volume] = useState(GOOSE_FIX_I_DEFAULTS.bucket3Volume);
  const [applyRidgeShift, setApplyRidgeShift] = useState(false);
  const [applyRisk, setApplyRisk] = useState(true);
  const [riskScoreModel, setRiskScoreModel] = useState('prod-v2.0.0');
  const [applySpikeRisk, setApplySpikeRisk] = useState(false);
  const [spikeRiskModel, setSpikeRiskModel] = useState('v9.0.0-upshift');

  // Fix II & IDA1 shared
  const [ida1ModelVersion, setIda1ModelVersion] = useState('ida1-v1.0.3');
  const [fixIIBucket1Volume, setFixIIBucket1Volume] = useState(20);
  const [fixIIBucket2Volume, setFixIIBucket2Volume] = useState(40);
  const [fixIIBucket3Volume, setFixIIBucket3Volume] = useState(60);
  const [maxAvgVolumePct, setMaxAvgVolumePct] = useState(30);
  const [maxPosition, setMaxPosition] = useState(60);

  // IDA1 specific
  const [marginTier1, setMarginTier1] = useState(GOOSE_MARGIN_TIER_DEFAULTS.marginTier1);
  const [marginTier2, setMarginTier2] = useState(GOOSE_MARGIN_TIER_DEFAULTS.marginTier2);
  const [marginTier3, setMarginTier3] = useState(GOOSE_MARGIN_TIER_DEFAULTS.marginTier3);
  const [marginTier4, setMarginTier4] = useState(GOOSE_MARGIN_TIER_DEFAULTS.marginTier4);

  // UI state
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [displayHours1to24, setDisplayHours1to24] = useState(true);
  // Fix II kill mode: 'none' | 'soft_kill' | 'hard_kill' — only meaningful on the Fix II tab
  const [activeFixIIKillMode, setActiveFixIIKillMode] = useState(null);
  const darkMode = typeof document !== 'undefined' && document.body.classList.contains('global-dark-mode');

  // Fetch base_action from DB when date changes
  const fetchBaseAction = useCallback(async (dateStr) => {
    try {
      const resp = await fetchGet(`/get-base-action?date=${dateStr}`);
      const data = await resp.json();
      if (data.status === 'success') {
        setBaseAction(data.base_action || '');
        setBaseVolume(data.volume || 0);
      }
    } catch (e) {
      // No data in DB — keep current defaults
    }
  }, [fetchGet]);

  useEffect(() => {
    if (date) fetchBaseAction(date);
  }, [date, fetchBaseAction]);

  useEffect(() => {
    if (probModelVersion !== V5_PROBABILISTIC_MODEL) {
      return;
    }

    setTrendFilterMargin((prev) => (
      prev === V5_FIX_I_DEFAULTS.trendFilterMargin ? prev : V5_FIX_I_DEFAULTS.trendFilterMargin
    ));
  }, [probModelVersion]);

  const isValidForm = (() => {
    if (!baseAction || baseAction === '') return false;
    if ((baseAction === 'Buy' || baseAction === 'Sell') && baseVolume <= 0) return false;
    return true;
  })();

  // deterministic: Fix II only — one price curve per quarter instead of the shared 124-step ladder
  const handleBuildOrders = async (killMode = 'none', deterministic = false) => {
    setActiveFixIIKillMode(deterministic ? `det_${killMode}` : killMode);
    setLoading(true);
    setError('');
    setResult(null);
    // Fix II Soft/Hard Kill must NOT depend on IDA1 predictions: drop the
    // IDA1 model version from the request so the backend won't query them.
    const isFixIIKill = activeTab === 'fix_ii' &&
                        (killMode === 'soft_kill' || killMode === 'hard_kill');
    try {
      const endpoint = deterministic ? '/build-goose-deterministic-fix2-orders' : '/build-goose-bison-orders';
      const response = await axiosPost(endpoint, {
        date,
        active_tab: activeTab,
        base_action: baseAction,
        base_volume: baseVolume,
        prob_model_version: probModelVersion,
        ida1_model_version: isFixIIKill ? null : ida1ModelVersion,
        fix_i_model: fixIModel,
        fix_ii_model: fixIIModel,
        trend_filter_margin: trendFilterMargin,
        bucket1_volume: bucket1Volume,
        bucket2_volume: bucket2Volume,
        bucket3_volume: bucket3Volume,
        fix_ii_ida1_bucket1_volume: fixIIBucket1Volume,
        fix_ii_ida1_bucket2_volume: fixIIBucket2Volume,
        fix_ii_ida1_bucket3_volume: fixIIBucket3Volume,
        max_avg_volume_pct: maxAvgVolumePct,
        max_position: maxPosition,
        margin_tier1: marginTier1,
        margin_tier2: marginTier2,
        margin_tier3: marginTier3,
        margin_tier4: marginTier4,
        apply_ridge_shift: applyRidgeShift,
        apply_risk: applyRisk,
        risk_score_model: applyRisk ? riskScoreModel : null,
        apply_spike_risk: applySpikeRisk,
        spike_risk_model: applySpikeRisk ? spikeRiskModel : null,
        fix_ii_kill_mode: killMode,
      });
      if (response.data.status === 'success') {
        setResult(response.data);
      } else {
        setError(response.data.message || 'Failed to build orders');
      }
    } catch (err) {
      setError(err.message || 'Failed to build orders');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'bos', deterministic = false) => {
    if (!result) return;
    setExporting(true);
    try {
      const endpoint = deterministic ? '/export-goose-deterministic-fix2-orders' : '/export-goose-bison-orders';
      const response = await axiosPost(endpoint, {
        orders_result: result,
        export_format: format,
      });
      if (response.data.status === 'success') {
        downloadBase64(response.data.file_data, response.data.filename);
        if (response.data.ida1_file_data) {
          downloadBase64(response.data.ida1_file_data, response.data.ida1_filename);
        }
      } else {
        setError(response.data.message || 'Failed to export');
      }
    } catch (err) {
      setError('Failed to export: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };

  const downloadBase64 = (b64, filename) => {
    const byteChars = atob(b64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header with auction tabs */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Goose Order Builder</h1>
          </div>
          <div className="flex gap-2">
            {[
              { key: 'fix_i', label: 'Fixing I' },
              { key: 'fix_ii', label: 'Fixing II' },
              { key: 'ida1', label: 'IDA1' },
            ].map(tab => (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setResult(null); setError(''); }}
                className={`px-4 py-2 font-medium rounded-lg transition-colors ${
                  activeTab === tab.key
                    ? 'bg-blue-600 text-white'
                    : darkMode
                      ? 'bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Parameters Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">

          {/* Common fields: Date, Base Action, Base Volume */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} /><span>Date</span>
              </label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <ShoppingCart size={16} /><span>Base Action</span>
              </label>
              <select value={baseAction} onChange={e => { setBaseAction(e.target.value); if (e.target.value === 'Idle') setBaseVolume(0); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">Select action...</option>
                <option value="Buy">Buy</option>
                <option value="Idle">Idle</option>
                <option value="Sell">Sell</option>
              </select>
            </div>
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Package size={16} /><span>Base Volume</span>
              </label>
              <input type="number" value={baseVolume} onChange={e => setBaseVolume(parseFloat(e.target.value) || 0)}
                disabled={baseAction === 'Idle' || baseAction === ''}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  baseAction === 'Idle' || baseAction === '' ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
            </div>
          </div>

          {/* ── Fix I specific fields ── */}
          {activeTab === 'fix_i' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Zap size={16} /><span>Probabilistic Model</span>
                  </label>
                  <select value={probModelVersion} onChange={e => setProbModelVersion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="v3.1.0">v3.1.0</option>
                    <option value="v5.0.0">v5.0.0</option>
                    <option value="v9.0.0-daily">v9.0.0-daily</option>
                    <option value="v11.0.0-tabpfn-3.5-daily-gpu">v11.0.0-tabpfn-3.5-daily-gpu</option>
                    <option value="v30-k">v30-k</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <TrendingUp size={16} /><span>Fix I Model</span>
                  </label>
                  <select value={fixIModel} onChange={e => setFixIModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="v1.0.0">v1.0.0</option>
                    <option value="v2.0.0">v2.0.0</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <TrendingUp size={16} /><span>Fix II Model</span>
                  </label>
                  <select value={fixIIModel} onChange={e => setFixIIModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                    <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${!applyRisk ? 'text-gray-400' : 'text-gray-700'}`}>
                    <Activity size={16} /><span>Price Collapse Risk Engine</span>
                  </label>
                  <select value={riskScoreModel} onChange={e => setRiskScoreModel(e.target.value)}
                    disabled={!applyRisk}
                    title={!applyRisk ? 'Enable Apply Price Collapse Risk to choose a Price Collapse Risk Engine' : ''}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!applyRisk ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}>
                    <option value="prod-v2.0.0">prod-v2.0.0</option>
                    <option value="collapse_logreg_v1_wf_pse_till_500">collapse_logreg_v1_wf_pse_till_500</option>
                  </select>
                </div>
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${!applySpikeRisk ? 'text-gray-400' : 'text-gray-700'}`}>
                    <Activity size={16} /><span>Price Spike Risk Engine</span>
                  </label>
                  <select value={spikeRiskModel} onChange={e => setSpikeRiskModel(e.target.value)}
                    disabled={!applySpikeRisk}
                    title={!applySpikeRisk ? 'Enable Apply Price Spike Risk to choose a Price Spike Risk Engine' : ''}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!applySpikeRisk ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}>
                    <option value="prod-spike-v2.0.0">prod-spike-v2.0.0</option>
                    <option value="prod-spike-ra-pure">prod-spike-ra-pure</option>
                    <option value="prod-spike-rz-champion">prod-spike-rz-champion</option>
                    <option value="prod-spike-r3-champion">prod-spike-r3-champion</option>
                    <option value="v9.0.0-upshift">v9.0.0-upshift</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Target size={16} /><span>Trend Filter Margin (PLN)</span>
                  </label>
                  <input type="number" value={trendFilterMargin} onChange={e => setTrendFilterMargin(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div className="flex items-end pb-1">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Apply Shift</span>
                    <button onClick={() => setApplyRidgeShift(!applyRidgeShift)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${applyRidgeShift ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <span className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full transition-transform ${applyRidgeShift ? 'translate-x-5' : ''}`} />
                    </button>
                  </label>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Apply Price Collapse Risk</span>
                    <button onClick={() => setApplyRisk(!applyRisk)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${applyRisk ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <span className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full transition-transform ${applyRisk ? 'translate-x-5' : ''}`} />
                    </button>
                  </label>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Apply Price Spike Risk</span>
                    <button onClick={() => setApplySpikeRisk(!applySpikeRisk)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${applySpikeRisk ? 'bg-amber-600' : 'bg-gray-300'}`}>
                      <span className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full transition-transform ${applySpikeRisk ? 'translate-x-5' : ''}`} />
                    </button>
                  </label>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Bucket Configuration</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Package size={16} /><span>Bucket 1 Volume (MWh)</span>
                    </label>
                    <input type="number" step="1" value={bucket1Volume} onChange={e => setBucket1Volume(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Package size={16} /><span>Bucket 2 Volume (MWh)</span>
                    </label>
                    <input type="number" step="1" value={bucket2Volume} onChange={e => setBucket2Volume(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Package size={16} /><span>Bucket 3 Volume (MWh)</span>
                    </label>
                    <input type="number" step="1" value={bucket3Volume} onChange={e => setBucket3Volume(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Fix II specific fields ── */}
          {activeTab === 'fix_ii' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Zap size={16} /><span>IDA1 Model</span>
                  </label>
                  <select value={ida1ModelVersion} onChange={e => setIda1ModelVersion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="ida1-v1.0.3">ida1-v1.0.3</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <TrendingUp size={16} /><span>Fix I Model</span>
                  </label>
                  <select value={fixIModel} onChange={e => setFixIModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="v1.0.0">v1.0.0</option>
                    <option value="v2.0.0">v2.0.0</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <TrendingUp size={16} /><span>Fix II Model</span>
                  </label>
                  <select value={fixIIModel} onChange={e => setFixIIModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                    <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Target size={16} /><span>Max Avg Volume (%)</span>
                  </label>
                  <input type="number" value={maxAvgVolumePct} onChange={e => setMaxAvgVolumePct(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Target size={16} /><span>Max Position (MW)</span>
                  </label>
                  <input type="number" value={maxPosition} onChange={e => setMaxPosition(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Fix II Bucket Volumes (MW, per quarter)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Package size={16} /><span>Bucket 1 (q20/q80)</span>
                    </label>
                    <input type="number" step="1" value={fixIIBucket1Volume} onChange={e => setFixIIBucket1Volume(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Package size={16} /><span>Bucket 2 (q10/q90)</span>
                    </label>
                    <input type="number" step="1" value={fixIIBucket2Volume} onChange={e => setFixIIBucket2Volume(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                      <Package size={16} /><span>Bucket 3 (q05/q95)</span>
                    </label>
                    <input type="number" step="1" value={fixIIBucket3Volume} onChange={e => setFixIIBucket3Volume(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Margin Tiers (PLN)</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">{'Price < 500'}</label>
                    <input type="number" value={marginTier1} onChange={e => setMarginTier1(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Price 500-700</label>
                    <input type="number" value={marginTier2} onChange={e => setMarginTier2(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Price 700-1000</label>
                    <input type="number" value={marginTier3} onChange={e => setMarginTier3(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">{'Price > 1000'}</label>
                    <input type="number" value={marginTier4} onChange={e => setMarginTier4(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── IDA1 specific fields ── */}
          {activeTab === 'ida1' && (
            <>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Margin Tiers (PLN)</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">{'Price < 500'}</label>
                    <input type="number" value={marginTier1} onChange={e => setMarginTier1(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Price 500-700</label>
                    <input type="number" value={marginTier2} onChange={e => setMarginTier2(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Price 700-1000</label>
                    <input type="number" value={marginTier3} onChange={e => setMarginTier3(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">{'Price > 1000'}</label>
                    <input type="number" value={marginTier4} onChange={e => setMarginTier4(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Build Button(s) */}
          <div className="mt-6">
            {activeTab === 'fix_i' ? (
              <button onClick={() => handleBuildOrders()} disabled={loading || !isValidForm}
                className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                {loading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div><span>Building...</span></>
                ) : (
                  <><ChevronRight className="w-5 h-5" /><span>Build Orders</span></>
                )}
              </button>
            ) : activeTab === 'fix_ii' ? (
              <div className="grid grid-cols-3 gap-3">
                <button onClick={() => handleBuildOrders('soft_kill')} disabled={loading || !isValidForm}
                  className="py-3 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                  {loading && activeFixIIKillMode === 'soft_kill' ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div><span>Building...</span></>
                  ) : (
                    <><ChevronRight className="w-5 h-5" /><span>Soft Kill Fixing II</span></>
                  )}
                </button>
                <button onClick={() => handleBuildOrders('hard_kill')} disabled={loading || !isValidForm}
                  className="py-3 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-900 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                  {loading && activeFixIIKillMode === 'hard_kill' ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div><span>Building...</span></>
                  ) : (
                    <><ChevronRight className="w-5 h-5" /><span>Hard Kill Fixing II</span></>
                  )}
                </button>
                <button onClick={() => handleBuildOrders('none')} disabled={loading || !isValidForm}
                  className="py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                  {loading && activeFixIIKillMode === 'none' ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div><span>Building...</span></>
                  ) : (
                    <><ChevronRight className="w-5 h-5" /><span>Build Orders</span></>
                  )}
                </button>
                <button onClick={() => handleBuildOrders('soft_kill', true)} disabled={loading || !isValidForm}
                  className="py-3 bg-white text-amber-700 border-2 border-amber-500 font-medium rounded-lg hover:bg-amber-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                  {loading && activeFixIIKillMode === 'det_soft_kill' ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent"></div><span>Building...</span></>
                  ) : (
                    <><ChevronRight className="w-5 h-5" /><span>Deterministic Soft Kill</span></>
                  )}
                </button>
                <button onClick={() => handleBuildOrders('hard_kill', true)} disabled={loading || !isValidForm}
                  className="py-3 bg-white text-slate-800 border-2 border-slate-800 font-medium rounded-lg hover:bg-slate-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                  {loading && activeFixIIKillMode === 'det_hard_kill' ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-800 border-t-transparent"></div><span>Building...</span></>
                  ) : (
                    <><ChevronRight className="w-5 h-5" /><span>Deterministic Hard Kill</span></>
                  )}
                </button>
                <button onClick={() => handleBuildOrders('none', true)} disabled={loading || !isValidForm}
                  className="py-3 bg-white text-blue-700 border-2 border-blue-600 font-medium rounded-lg hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                  {loading && activeFixIIKillMode === 'det_none' ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div><span>Building...</span></>
                  ) : (
                    <><ChevronRight className="w-5 h-5" /><span>Build Deterministic Orders</span></>
                  )}
                </button>
              </div>
            ) : (
              <button onClick={() => handleBuildOrders()} disabled={loading || !isValidForm}
                className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
                {loading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div><span>Building Orders...</span></>
                ) : (
                  <><ChevronRight className="w-5 h-5" /><span>Build Orders</span></>
                )}
              </button>
            )}
          </div>

          {/* Hour display toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={displayHours1to24} onChange={e => setDisplayHours1to24(e.target.checked)} className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
            <div className="mt-0.5"><AlertCircle className="h-5 w-5 text-red-500" /></div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* ── Fix I Results ── */}
        {result && result.fix_i_orders && activeTab === 'fix_i' && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-6">
                  <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <Info className="w-4 h-4" />Fix I Orders for {result.date}
                  </h2>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">q50: <span className="font-medium text-gray-800">{result.metadata.avg_q50?.toFixed(2) || '-'}</span></span>
                    <span className="text-gray-500">Fix I: <span className="font-medium text-gray-800">{result.metadata.avg_fix_i?.toFixed(2) || '-'}</span></span>
                    <span className="text-gray-500">Fix II: <span className="font-medium text-gray-800">{result.metadata.avg_fix_ii?.toFixed(2) || '-'}</span></span>
                    {result.metadata.apply_ridge_shift && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Shift</span>}
                    {result.metadata.apply_risk && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                        Collapse{result.metadata.risk_shift_avg != null ? ` ${result.metadata.risk_shift_avg.toFixed(1)}` : ''}
                      </span>
                    )}
                    {result.metadata.apply_spike_risk && (
                      <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                        Spike{result.metadata.spike_risk_shift_avg != null ? ` ${result.metadata.spike_risk_shift_avg.toFixed(1)}` : ''}
                      </span>
                    )}
                    {/* With Apply Price Spike Risk on the price waits for the spike engine:
                        the run and spike issue the orders were built from. */}
                    {result.metadata.prob_issue_date && (
                      <span className="text-xs text-gray-500">
                        Run {warsawTime(result.metadata.prob_issue_date)} · {result.metadata.spike_risk_model} {warsawTime(result.metadata.spike_issue_date)}
                        {result.metadata.latest_prob_issue_date !== result.metadata.prob_issue_date && (
                          <span className="text-amber-600"> · run {warsawTime(result.metadata.latest_prob_issue_date)} waiting for {result.metadata.spike_risk_model}</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleExport('bos')} disabled={exporting}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                    <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export in BOŚ'}
                  </button>
                  <button onClick={() => handleExport('noble')} disabled={exporting}
                    className="px-3 py-1.5 bg-orange-300 text-gray-800 text-sm font-medium rounded-lg hover:bg-orange-400 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                    <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export in Noble'}
                  </button>
                  <button onClick={() => handleExport('sapri')} disabled={exporting}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                    <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export in SAPRI'}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-4 text-xs">
                {result.metadata.base_action !== 'Idle' && <span className="text-blue-700 font-medium">BASE: {result.metadata.base_volume} MWh</span>}
                <span className="text-green-700 font-medium">B1 ({result.metadata.bucket1_volume} MWh)</span>
                <span className="text-yellow-700 font-medium">B2 ({result.metadata.bucket2_volume} MWh)</span>
                <span className="text-orange-700 font-medium">B3 ({result.metadata.bucket3_volume} MWh)</span>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">Hour</th>
                      {result.metadata.base_action !== 'Idle' && <th className="px-2 py-1.5 text-right font-medium text-blue-700 bg-blue-50">BASE</th>}
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-green-50">B1 Buy</th>
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-green-50">B1 Sell</th>
                      <th className="px-2 py-1.5 text-right font-medium text-yellow-700 bg-yellow-50">B2 Buy</th>
                      <th className="px-2 py-1.5 text-right font-medium text-yellow-700 bg-yellow-50">B2 Sell</th>
                      <th className="px-2 py-1.5 text-right font-medium text-orange-700 bg-orange-50">B3 Buy</th>
                      <th className="px-2 py-1.5 text-right font-medium text-orange-700 bg-orange-50">B3 Sell</th>
                      <th className="px-2 py-1.5 text-center font-medium text-gray-600">Trend</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-600">Shift</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-600">Collapse</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-600">Spike</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.fix_i_orders.map((order, i) => {
                      const buyClass = 'text-green-700';
                      const sellClass = 'text-red-700';
                      return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-2 py-1 text-gray-900 font-medium sticky left-0 bg-white z-10">{displayHours1to24 ? order.hour + 1 : order.hour}</td>
                        {result.metadata.base_action !== 'Idle' && (
                          <td className={`px-2 py-1 text-right bg-blue-50/50 ${order.base?.action === 'Buy' ? 'text-green-700' : 'text-red-700'}`}>
                            {order.base?.price?.toFixed(2) || '-'}
                          </td>
                        )}
                        <td className={`px-2 py-1 text-right bg-green-50/30 ${buyClass}`}>{order.bucket1_buy.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-right bg-green-50/30 ${sellClass}`}>{order.bucket1_sell.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-right bg-yellow-50/30 ${buyClass}`}>{order.bucket2_buy.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-right bg-yellow-50/30 ${sellClass}`}>{order.bucket2_sell.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-right bg-orange-50/30 ${buyClass}`}>{order.bucket3_buy.toFixed(2)}</td>
                        <td className={`px-2 py-1 text-right bg-orange-50/30 ${sellClass}`}>{order.bucket3_sell.toFixed(2)}</td>
                        <td className="px-2 py-1 text-center">
                          {order.shift_direction === -1 ? <span className="text-green-600">up</span> : order.shift_direction === 1 ? <span className="text-red-600">down</span> : ''}
                        </td>
                        <td className="px-2 py-1 text-right text-gray-500">{order.ridge_shift !== 0 ? order.ridge_shift.toFixed(1) : ''}</td>
                        <td className={`px-2 py-1 text-right ${order.risk_shift && order.risk_shift < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                          {order.risk_shift !== 0 && order.risk_shift != null ? order.risk_shift.toFixed(1) : ''}
                        </td>
                        <td className={`px-2 py-1 text-right ${order.spike_risk_shift && order.spike_risk_shift > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                          {order.spike_risk_shift !== 0 && order.spike_risk_shift != null ? order.spike_risk_shift.toFixed(1) : ''}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Fix II Results ── */}
        {result && result.fix_ii_orders && activeTab === 'fix_ii' && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-6">
                  <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <Info className="w-4 h-4" />Fix II Orders for {result.date}
                  </h2>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">Model: <span className="font-medium text-gray-800">{result.metadata.ida1_model_version}</span></span>
                    <span className="text-gray-500">Quarters: <span className="font-medium text-gray-800">{result.fix_ii_orders.length}</span></span>
                    {result.fix_ii_deterministic && (
                      <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded">
                        Deterministic · max {result.fix_ii_deterministic.metadata.max_steps_used} steps
                      </span>
                    )}
                    {result.fix_ii_deterministic?.metadata.clamped_prices.length > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded"
                        title={result.fix_ii_deterministic.metadata.clamped_prices.map(c => `${c.label}: ${c.price} → ${c.clamped_to}`).join('\n')}>
                        {result.fix_ii_deterministic.metadata.clamped_prices.length} price(s) clamped to limits
                      </span>
                    )}
                  </div>
                </div>
                {result.fix_ii_deterministic ? (
                <div className="flex gap-2">
                  <button onClick={() => handleExport('bos', true)} disabled={exporting}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                    <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export Deterministic'}
                  </button>
                  <button onClick={() => handleExport('sapri', true)} disabled={exporting}
                    className="px-3 py-1.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                    <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export Deterministic SAPRI'}
                  </button>
                </div>
                ) : (
                <div className="flex gap-2">
                  <button onClick={() => handleExport('bos')} disabled={exporting}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                    <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export'}
                  </button>
                  {result.fix_ii_optimized && (
                    <button onClick={() => handleExport('matrix')} disabled={exporting}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                      <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export Matrix'}
                    </button>
                  )}
                  {result.fix_ii_optimized && (
                    <button onClick={() => handleExport('sapri')} disabled={exporting}
                      className="px-3 py-1.5 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                      <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export SAPRI'}
                    </button>
                  )}
                </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 bg-teal-50 text-xs text-teal-700 font-medium flex gap-4">
                {result.metadata.historia_file && (
                  <span>Source: {result.metadata.historia_file}</span>
                )}
                <span>Base: {result.metadata.base_action} {result.metadata.base_action !== 'Idle' ? `${result.metadata.base_volume} MW` : ''}</span>
                <span>Bucket vol/qtr: B1={result.metadata.fix_ii_ida1_bucket1_volume}, B2={result.metadata.fix_ii_ida1_bucket2_volume}, B3={result.metadata.fix_ii_ida1_bucket3_volume} MW</span>
              </div>
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 z-20">
                    <tr className="border-b border-gray-100">
                      <th className="px-2 py-1 sticky left-0 bg-gray-50 z-10"></th>
                      <th colSpan="2" className="px-2 py-1 text-center font-semibold text-purple-800 bg-purple-50">Fix I (Historia)</th>
                      <th className="px-2 py-1"></th>
                      <th colSpan="3" className="px-2 py-1 text-center font-semibold text-orange-800 bg-orange-50">Margin Close</th>
                      <th colSpan="3" className="px-2 py-1 text-center font-semibold text-blue-800 bg-blue-50">Q40-60 Close</th>
                      <th colSpan="3" className="px-2 py-1 text-center font-semibold text-green-800 bg-green-50">Buy Buckets</th>
                      <th colSpan="3" className="px-2 py-1 text-center font-semibold text-red-800 bg-red-50">Sell Buckets</th>
                      <th className="px-2 py-1"></th>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">Quarter</th>
                      <th className="px-2 py-1.5 text-center font-medium text-purple-700 bg-purple-50" title="Fix I direction and volume">Fix I</th>
                      <th className="px-2 py-1.5 text-right font-medium text-purple-700 bg-purple-50" title="Fix I price: Historia fill, or the 15-min TGE price when there is no fill or in kill mode">Price</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-700" title="Net position (base + Fix I)">Net</th>
                      <th className="px-2 py-1.5 text-center font-medium text-orange-700 bg-orange-50">Dir</th>
                      <th className="px-2 py-1.5 text-right font-medium text-orange-700 bg-orange-50">Price</th>
                      <th className="px-2 py-1.5 text-right font-medium text-orange-700 bg-orange-50">Vol</th>
                      <th className="px-2 py-1.5 text-center font-medium text-blue-700 bg-blue-50">Dir</th>
                      <th className="px-2 py-1.5 text-right font-medium text-blue-700 bg-blue-50">Price</th>
                      <th className="px-2 py-1.5 text-right font-medium text-blue-700 bg-blue-50">Vol</th>
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-green-50">q20</th>
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-green-50">q10</th>
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-green-50">q05</th>
                      <th className="px-2 py-1.5 text-right font-medium text-red-700 bg-red-50">q80</th>
                      <th className="px-2 py-1.5 text-right font-medium text-red-700 bg-red-50">q90</th>
                      <th className="px-2 py-1.5 text-right font-medium text-red-700 bg-red-50">q95</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-600">Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.fix_ii_orders.map((order, i) => {
                      const isOddHour = order.hour % 2 === 1;
                      const showFixI = order.qh === 0; // Show Fix I info only on first quarter of each hour
                      return (
                        <tr key={i} className={isOddHour ? 'bg-gray-50' : ''}>
                          <td className={`px-2 py-1 font-medium sticky left-0 z-10 ${isOddHour ? 'bg-gray-50' : 'bg-white'} text-gray-900`}>{order.label}</td>
                          <td className={`px-2 py-1 text-center bg-purple-50/30 ${order.fix_i_direction === 'Buy' ? 'text-green-700' : order.fix_i_direction === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                            {showFixI && order.fix_i_direction ? `${order.fix_i_direction === 'Buy' ? 'B' : 'S'} ${order.fix_i_volume}` : ''}
                          </td>
                          <td className="px-2 py-1 text-right bg-purple-50/30 text-purple-700">
                            {showFixI && order.fix_i_price != null ? order.fix_i_price.toFixed(2) : ''}
                          </td>
                          <td className={`px-2 py-1 text-right font-medium ${order.net_position > 0 ? 'text-green-700' : order.net_position < 0 ? 'text-red-700' : 'text-gray-400'}`}>
                            {showFixI ? order.net_position : ''}
                          </td>
                          <td className={`px-2 py-1 text-center bg-orange-50/30 ${order.margin_close_action === 'Buy' ? 'text-green-700' : order.margin_close_action === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                            {order.margin_close_action ? (order.margin_close_action === 'Buy' ? 'B' : 'S') : '-'}
                          </td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${order.margin_close_action === 'Buy' ? 'text-green-700' : order.margin_close_action === 'Sell' ? 'text-red-700' : 'text-gray-600'}`}>
                            {order.margin_close_price != null && order.margin_close_volume > 0 ? order.margin_close_price.toFixed(2) : '-'}
                          </td>
                          <td className="px-2 py-1 text-right bg-orange-50/30 text-orange-700">
                            {order.margin_close_volume > 0 ? order.margin_close_volume : '-'}
                          </td>
                          <td className={`px-2 py-1 text-center bg-blue-50/30 ${order.q4060_close_action === 'Buy' ? 'text-green-700' : order.q4060_close_action === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                            {order.q4060_close_action ? (order.q4060_close_action === 'Buy' ? 'B' : 'S') : '-'}
                          </td>
                          <td className={`px-2 py-1 text-right bg-blue-50/30 ${order.q4060_close_action === 'Buy' ? 'text-green-700' : order.q4060_close_action === 'Sell' ? 'text-red-700' : 'text-gray-600'}`}>
                            {order.q4060_close_price != null && order.q4060_close_volume > 0 ? order.q4060_close_price.toFixed(2) : '-'}
                          </td>
                          <td className="px-2 py-1 text-right bg-blue-50/30 text-blue-700">
                            {order.q4060_close_volume > 0 ? order.q4060_close_volume : '-'}
                          </td>
                          <td className={`px-2 py-1 text-right ${order.skip_buy || order.buy_bucket1_volume === 0 ? 'text-gray-400 line-through' : 'text-green-700'}`}>{order.buy_bucket1_price != null ? order.buy_bucket1_price.toFixed(2) : '-'}</td>
                          <td className={`px-2 py-1 text-right ${order.skip_buy || order.buy_bucket2_volume === 0 ? 'text-gray-400 line-through' : 'text-green-700'}`}>{order.buy_bucket2_price != null ? order.buy_bucket2_price.toFixed(2) : '-'}</td>
                          <td className={`px-2 py-1 text-right ${order.skip_buy || order.buy_bucket3_volume === 0 ? 'text-gray-400 line-through' : 'text-green-700'}`}>{order.buy_bucket3_price != null ? order.buy_bucket3_price.toFixed(2) : '-'}</td>
                          <td className={`px-2 py-1 text-right ${order.skip_sell || order.sell_bucket1_volume === 0 ? 'text-gray-400 line-through' : 'text-red-700'}`}>{order.sell_bucket1_price != null ? order.sell_bucket1_price.toFixed(2) : '-'}</td>
                          <td className={`px-2 py-1 text-right ${order.skip_sell || order.sell_bucket2_volume === 0 ? 'text-gray-400 line-through' : 'text-red-700'}`}>{order.sell_bucket2_price != null ? order.sell_bucket2_price.toFixed(2) : '-'}</td>
                          <td className={`px-2 py-1 text-right ${order.skip_sell || order.sell_bucket3_volume === 0 ? 'text-gray-400 line-through' : 'text-red-700'}`}>{order.sell_bucket3_price != null ? order.sell_bucket3_price.toFixed(2) : '-'}</td>
                          <td className="px-2 py-1 text-right text-gray-500">{order.position_limit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── IDA1 Results ── */}
        {result && result.ida1_orders && activeTab === 'ida1' && (
          <>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-6">
                  <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <Info className="w-4 h-4" />IDA1 Closing Orders for {result.date}
                    {result.metadata.eur_pln_rate && (
                      <span className="text-sm font-normal text-indigo-600 ml-2">EUR/PLN: {result.metadata.eur_pln_rate.toFixed(4)}</span>
                    )}
                  </h2>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">Quarters: <span className="font-medium text-gray-800">{result.ida1_orders.length}</span></span>
                  </div>
                </div>
                <button onClick={() => handleExport('bos')} disabled={exporting}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-1.5 transition-colors">
                  <FileSpreadsheet className="w-4 h-4" />{exporting ? 'Exporting...' : 'Export'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-200 bg-orange-50 text-xs text-orange-700 font-medium flex gap-4">
                {result.metadata.historia_file && (
                  <span>Source: {result.metadata.historia_file}</span>
                )}
                <span>Base: {result.metadata.base_action} {result.metadata.base_action !== 'Idle' ? `${result.metadata.base_volume} MW` : ''}</span>
              </div>
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 z-20">
                    <tr className="border-b border-gray-100">
                      <th className="px-2 py-1 sticky left-0 bg-gray-50 z-10"></th>
                      <th colSpan="2" className="px-2 py-1 text-center font-semibold text-purple-800 bg-purple-50">Fix I (Historia)</th>
                      <th colSpan="2" className="px-2 py-1 text-center font-semibold text-teal-800 bg-teal-50">Fix II (Historia)</th>
                      <th className="px-2 py-1"></th>
                      <th colSpan="6" className="px-2 py-1 text-center font-semibold text-orange-800 bg-orange-50">IDA1 Closing</th>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">Quarter</th>
                      <th className="px-2 py-1.5 text-center font-medium text-purple-700 bg-purple-50">Fix I</th>
                      <th className="px-2 py-1.5 text-right font-medium text-purple-700 bg-purple-50">Price</th>
                      <th className="px-2 py-1.5 text-center font-medium text-teal-700 bg-teal-50">Fix II</th>
                      <th className="px-2 py-1.5 text-right font-medium text-teal-700 bg-teal-50">Price</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-700">Net</th>
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-orange-50">Buy (EUR)</th>
                      <th className="px-2 py-1.5 text-right font-medium text-green-700 bg-orange-50">Buy (PLN)</th>
                      <th className="px-2 py-1.5 text-right font-medium text-red-700 bg-orange-50">Sell (PLN)</th>
                      <th className="px-2 py-1.5 text-right font-medium text-red-700 bg-orange-50">Sell (EUR)</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-600 bg-orange-50">Vol</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-600 bg-orange-50">Margin (PLN)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.ida1_orders.map((order, i) => {
                      const isOddHour = order.hour % 2 === 1;
                      const isFirstQh = order.qh === 0;
                      const noFixII = order.buy_price == null;
                      // net_position > 0 means long → need to sell to close → gray out buy
                      // net_position < 0 means short → need to buy to close → gray out sell
                      // net_position === 0 or no Fix II price → gray out both
                      const grayBuy = noFixII || order.net_position >= 0;
                      const graySell = noFixII || order.net_position <= 0;
                      const grayStyle = 'text-gray-300 line-through';
                      return (
                        <tr key={i} className={isOddHour ? 'bg-gray-50' : ''}>
                          <td className={`px-2 py-1 font-medium sticky left-0 z-10 ${isOddHour ? 'bg-gray-50' : 'bg-white'} text-gray-900`}>{order.label}</td>
                          {/* Fix I (Historia) — one row per hour */}
                          {isFirstQh && (
                            <>
                              <td rowSpan={4} className={`px-2 py-1 text-center bg-purple-50/30 align-middle ${order.fix_i_direction === 'Buy' ? 'text-green-700' : order.fix_i_direction === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                                {order.fix_i_direction ? `${order.fix_i_direction === 'Buy' ? 'B' : 'S'} ${order.fix_i_volume}` : ''}
                              </td>
                              <td rowSpan={4} className="px-2 py-1 text-right bg-purple-50/30 text-purple-700 align-middle">
                                {order.fix_i_price != null ? order.fix_i_price.toFixed(2) : ''}
                              </td>
                            </>
                          )}
                          {/* Fix II (Historia) */}
                          <td className={`px-2 py-1 text-center bg-teal-50/30 ${order.fix_ii_direction === 'Buy' ? 'text-green-700' : order.fix_ii_direction === 'Sell' ? 'text-red-700' : 'text-gray-400'}`}>
                            {order.fix_ii_direction ? `${order.fix_ii_direction === 'Buy' ? 'B' : 'S'} ${order.fix_ii_volume}` : '-'}
                          </td>
                          <td className="px-2 py-1 text-right bg-teal-50/30 text-teal-700">
                            {order.fix_ii_price != null ? order.fix_ii_price.toFixed(2) : '-'}
                          </td>
                          {/* Net */}
                          <td className={`px-2 py-1 text-right font-medium ${order.net_position > 0 ? 'text-green-700' : order.net_position < 0 ? 'text-red-700' : 'text-gray-400'}`}>
                            {order.net_position}
                          </td>
                          {/* IDA1 Closing */}
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${grayBuy ? grayStyle : 'text-green-700'}`}>{order.buy_price_eur?.toFixed(1) ?? '-'}</td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${grayBuy ? grayStyle : 'text-green-700'}`}>{order.buy_price?.toFixed(1) ?? '-'}</td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${graySell ? grayStyle : 'text-red-700'}`}>{order.sell_price?.toFixed(1) ?? '-'}</td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${graySell ? grayStyle : 'text-red-700'}`}>{order.sell_price_eur?.toFixed(1) ?? '-'}</td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${noFixII ? grayStyle : 'text-gray-700'}`}>{order.volume ?? '-'}</td>
                          <td className={`px-2 py-1 text-right bg-orange-50/30 ${noFixII ? grayStyle : 'text-gray-500'}`}>{order.margin ?? '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default GooseOrderBuilder;
